import React, { useRef, useEffect, useState } from 'react';
import { outlineTexts } from '../contexts/LangZC';

function ProductZCOutline({ lang = 'ko', isMobile = false }) {
  const sectionRef = useRef(null);
  const [imgVisible, setImgVisible] = useState(false);

  // 하드디스크 선 번쩍임 상태
  const [blink, setBlink] = useState(false);

  // 아이콘 박스 내려오는 액션 상태
  const [boxDrop, setBoxDrop] = useState(false);

  // 해커 아이콘 애니메이션 상태
  const [hackerPhase, setHackerPhase] = useState('idle'); // idle, moving, hit, shake, return

  // 흔들림용 카운터
  const [shakeStep, setShakeStep] = useState(0);

  // 해커 활성화 상태 (마우스 오버/아웃)
  const [hackerActive, setHackerActive] = useState(false);

  // 사람/한글 파일 애니메이션 상태
  const [aniStep, setAniStep] = useState('idle');
  const [personX, setPersonX] = useState(-90);
  const [hangeulScale, setHangeulScale] = useState(1);

  // 해커 아이콘 X좌표 상태 추가
  const [hackerX, setHackerX] = useState(420); // 중앙(420)에서 시작

  // 마우스 활성화 상태
  const [mouseActive, setMouseActive] = useState(false);

  // ZONECENTRAL 개요 박스가 화면에 보일 때만 boxDrop 트리거
  useEffect(() => {
    if (imgVisible) {
      setBoxDrop(true);
    } else {
      setBoxDrop(false);
    }
  }, [imgVisible]);

  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        setImgVisible(entry.isIntersecting);
      },
      { threshold: 0 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // 하드디스크 선 번쩍임 효과
  useEffect(() => {
    const interval = setInterval(() => setBlink(b => !b), 700); // 0.7초로 속도 늦춤
    return () => clearInterval(interval);
  }, []);

  // boxDrop이 true가 되면 사람 아이콘이 중앙에서 왼쪽으로 이동하는 애니메이션 실행
  useEffect(() => {
    if (boxDrop) {
      setPersonX(220); // 중앙 위치로 이동
      const timer = setTimeout(() => setPersonX(-90), 600); // 0.6초 후 왼쪽으로 이동
      return () => clearTimeout(timer);
    } else {
      setPersonX(-90); // 초기 위치(왼쪽)
    }
  }, [boxDrop]);

  // boxDrop이 true가 되면 사람/한글 파일 애니메이션 시작
  useEffect(() => {
    if (boxDrop) setAniStep('person-center');
    else setAniStep('idle');
  }, [boxDrop]);

  // 사람-한글 파일 애니메이션 시퀀스
  useEffect(() => {
    let timer;
    if (aniStep === 'person-center') {
      setPersonX(220); // 사람: 중앙
      timer = setTimeout(() => setAniStep('hangeul-big'), 400);
    } else if (aniStep === 'hangeul-big') {
      timer = setTimeout(() => setAniStep('hangeul-small'), 400);
    } else if (aniStep === 'hangeul-small') {
      timer = setTimeout(() => setAniStep('person-left'), 400);
    } else if (aniStep === 'person-left') {
      setPersonX(-90); // 사람: 왼쪽으로 이동
    } else if (aniStep === 'idle') {
      setPersonX(-90);
    }
    return () => clearTimeout(timer);
  }, [aniStep]);

  // 마우스 올릴 때만 해커 애니메이션 반복, 내리면 즉시 idle로 초기화
  useEffect(() => {
    if (!hackerActive) {
      setHackerPhase('idle');
      setShakeStep(0);
    } else {
      setHackerPhase('moving');
    }
  }, [hackerActive]);

  // 해커 아이콘 애니메이션 시퀀스 (반복은 hackerActive가 true일 때만)
  useEffect(() => {
    let timer;
    if (!hackerActive) return; // 마우스가 없으면 아무것도 하지 않음

    if (hackerPhase === 'moving') {
      timer = setTimeout(() => setHackerPhase('hit'), 700); // 350 → 700ms (2배 느리게)
    } else if (hackerPhase === 'hit') {
      setShakeStep(0);
      timer = setTimeout(() => setHackerPhase('shake'), 100); // 50 → 100ms (2배 느리게)
    } else if (hackerPhase === 'shake') {
      if (shakeStep < 7) {
        timer = setTimeout(() => setShakeStep(s => s + 1), 80); // 40 → 80ms (2배 느리게)
      } else {
        timer = setTimeout(() => setHackerPhase('return'), 200); // 100 → 200ms (2배 느리게)
      }
    } else if (hackerPhase === 'return') {
      timer = setTimeout(() => setHackerPhase('moving'), 700); // 350 → 700ms (2배 느리게)
    }
    return () => clearTimeout(timer);
  }, [hackerPhase, shakeStep, hackerActive]);

  // 해커 아이콘 위치 계산
  let hackerY = 110, hackerColor = '#fff', hackerHandColor = '#fff';
  if (boxDrop) {
    if (hackerPhase === 'moving') {
      hackerColor = '#fff';
    } else if (hackerPhase === 'hit' || hackerPhase === 'shake') {
      hackerColor = '#e53935';
      hackerHandColor = '#e53935';
    } else if (hackerPhase === 'return') {
      hackerColor = '#fff';
    }
  }

  return (
    <section
      ref={sectionRef}
      style={{
        width: '100vw',
        maxWidth: '100vw',
        background: '#3a3570',
        borderRadius: 0,
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        marginBottom: isMobile ? 30 : 40,
        padding: isMobile ? '32px 20px' : '48px 40px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        minHeight: isMobile ? 'auto' : 100
      }}
    >
      {/* 애니메이션 제어용 컴포넌트 */}
      <ProductZCOutline_StartAni boxDrop={boxDrop} setPersonX={setPersonX} setHackerX={setHackerX} />

      <span style={{
        fontSize: isMobile ? '1.6rem' : '2rem',
        fontWeight: 700,
        color: '#fff',
        marginBottom: isMobile ? 20 : 16,
        textAlign: 'center',
        width: '100%',
        lineHeight: isMobile ? 1.3 : 1.2
      }}>
        {outlineTexts.title[lang]}
      </span>
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'center' : 'center',
        gap: isMobile ? 20 : 24,
        margin: '0 0 24px 0',
        width: '100%'
      }}
      className="zc-outline-row"
      >
        <div style={{
          color: '#fff',
          fontSize: isMobile ? '1.2rem' : '1.45rem',
          fontWeight: 700,
          whiteSpace: 'pre-line',
          flex: isMobile ? 'none' : 1,
          minWidth: 0,
          width: isMobile ? '100%' : 'auto',
          textAlign: isMobile ? 'center' : 'left',
          transition: 'transform 0.8s cubic-bezier(0.23, 1, 0.32, 1), opacity 0.8s cubic-bezier(0.23, 1, 0.32, 1)',
          transform: imgVisible ? 'translateX(0)' : (isMobile ? 'translateY(-50px)' : 'translateX(-120vw)'),
          opacity: imgVisible ? 1 : 0,
          marginBottom: isMobile ? 20 : 12
        }}>
          {outlineTexts.subtitle[lang]}
          <br /><br />
          <span style={{
            fontSize: isMobile ? '1rem' : '1.15rem',
            fontWeight: 500
          }}>
            {outlineTexts.message[lang]}
          </span>
        </div>
        <div
          style={{
            flex: isMobile ? 'none' : 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minWidth: 0,
            width: isMobile ? '100%' : 'auto',
            background: 'transparent'
          }}
          onMouseEnter={() => !isMobile && setMouseActive(true)}
          onMouseLeave={() => !isMobile && setMouseActive(false)}
        >
          <svg
            width={isMobile ? "320" : "720"}
            height={isMobile ? "240" : "480"}
            viewBox="0 0 900 480"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ 
              display: 'block',
              maxWidth: '100%',
              height: 'auto'
            }}
          >
            {/* 사각 박스(아이콘 포함) - 제일 먼저 배치! */}
            <ProductZCOutline_Harddisk boxDrop={boxDrop} hangeulScale={hangeulScale} blink={blink} hackerPhase={hackerPhase} />
            {/* 사람 아이콘 */}
            <ProductZCOutline_User personX={personX} />
            {/* 사람-하드디스크 간격용 투명 사각형 */}
            <rect x="150" y="0" width="40" height="240" fill="none"/>
            {/* 하드디스크-해커 간격용 투명 사각형 */}
            <rect x="440" y="0" width="80" height="240" fill="none"/>
            {/* 해커 아이콘 */}
            <ProductZCOutline_Haeker hackerX={hackerX} hackerPhase={hackerPhase} />
          </svg>
        </div>
      </div>
      <ProductZCOutline_MouseupdownAni
        mouseActive={mouseActive}
        setPersonX={setPersonX}
        setHackerX={setHackerX}
        setHackerPhase={setHackerPhase}
        setHangeulScale={setHangeulScale}
      />
    </section>
  );
}

function ProductZCOutline_StartAni({ boxDrop, setPersonX, setHackerX }) {
  useEffect(() => {
    let timer1;
    if (boxDrop) {
      setPersonX(220);   // 사람: 중앙
      setHackerX(420);   // 해커: 사각 박스 중앙(뒤)
      timer1 = setTimeout(() => {
        setPersonX(-90);   // 사람: 왼쪽
        setHackerX(700);   // 해커: 오른쪽
      }, 600);
    } else {
      setPersonX(220);
      setHackerX(420);
    }
    return () => clearTimeout(timer1);
  }, [boxDrop, setPersonX, setHackerX]);
  return null;
}

function ProductZCOutline_MouseupdownAni({ mouseActive, setPersonX, setHackerX, setHackerPhase, setHangeulScale }) {
  useEffect(() => {
    let timer;
    let phase = 0;
    let shakeCount = 0;
    const shakeMax = 3;

    function loop() {
      if (!mouseActive) return;
      if (phase === 0) {
        setPersonX(220); // 사람 중앙
        setHackerX(700);
        setHackerPhase('idle');
        timer = setTimeout(() => { phase = 1; loop(); }, 400);
      } else if (phase === 1) {
        setHangeulScale(4); // 한글 파일 커짐
        timer = setTimeout(() => { setHangeulScale(1); phase = 2; loop(); }, 400);
      } else if (phase === 2) {
        setPersonX(-90); // 사람 왼쪽으로 이동
        timer = setTimeout(() => { phase = 3; loop(); }, 400);
      } else if (phase === 3) {
        setHackerX(420);
        setHackerPhase('idle');
        shakeCount = 0;
        timer = setTimeout(() => { phase = 4; loop(); }, 400);
      } else if (phase === 4) {
        setHackerPhase('shake');
        if (shakeCount < shakeMax * 2) {
          setHackerX(420 + (shakeCount % 2 === 0 ? -20 : 20));
          shakeCount++;
          timer = setTimeout(loop, 120);
        } else {
          setHackerX(420);
          setHackerPhase('idle');
          timer = setTimeout(() => { phase = 5; loop(); }, 120);
        }
      } else if (phase === 5) {
        setHackerX(700);
        setHackerPhase('idle');
        timer = setTimeout(() => { phase = 0; loop(); }, 400);
      }
    }

    if (mouseActive) {
      phase = 0;
      loop();
    } else {
      setPersonX(-90);
      setHackerX(700);
      setHackerPhase('idle');
      setHangeulScale(1); // 마우스가 내려가면 한글 파일 아이콘 원래 크기로
    }

    return () => clearTimeout(timer);
  }, [mouseActive, setPersonX, setHackerX, setHackerPhase, setHangeulScale]);
  return null;
}

function ProductZCOutline_User({ personX }) {
  return (
    <g
      style={{
        transition: 'transform 0.4s cubic-bezier(0.23, 1, 0.32, 1)',
        transform: `translate(${personX}px, 200px)`
      }}
    >
      {/* 사람 머리 - 회색 채움 */}
      <ellipse cx="185" cy="-30" rx="36" ry="40" stroke="#fff" strokeWidth="10" fill="#bbb"/>
      {/* 사람 몸통 - 회색 채움 */}
      <path d="M120 90c0-44 32-80 72-80s72 36 72 80" stroke="#fff" strokeWidth="10" fill="#bbb"/>
    </g>
  );
}

function ProductZCOutline_Harddisk({ boxDrop, hangeulScale, blink }) {
  // 흔들림일 때만 빨간색, 아니면 흰색
  // const borderColor = hackerPhase === 'shake' ? '#e53935' : '#fff';
  // const borderColor = '#e53935';
  const borderColor = blink ? '#ff5252' : '#e53935';

  return (
    <g
      style={{
        transition: 'transform 1s cubic-bezier(0.23, 1, 0.32, 1)',
        transform: boxDrop
          ? 'translate(40px, 0)'
          : 'translate(40px, -480px)'
      }}
    >
      {/* 사각형 박스 */}
      <rect
        x="220"
        y="20"
        width="400"
        height="440"
        rx="16"
        stroke={borderColor}
        strokeWidth="8"
        fill="#3a3570"
        style={{ transition: 'stroke 0.2s' }}
      />
      {/* 파일 아이콘들 */}
      <g style={{
        transform: 'translateX(20px)', // 전체 파일 아이콘을 오른쪽으로 20px 이동
        transition: 'transform 0.4s cubic-bezier(0.23,1,0.32,1)'
      }}>
        {/* 엑셀 파일 아이콘 */}
        <g>
          <rect
            x="260"
            y="336"
            width="48"
            height="64"
            rx="8"
            fill="#fff"
            stroke="#43a047"
            strokeWidth="3"
          />
          <text
            x="284"
            y="376"
            fontSize="22"
            fontWeight="bold"
            fill="#43a047"
            textAnchor="middle"
            alignmentBaseline="middle"
            style={{ fontFamily: 'Arial, sans-serif' }}
          >X</text>
        </g>
        {/* 워드 파일 아이콘 */}
        <g>
          <rect
            x="340"
            y="336"
            width="48"
            height="64"
            rx="8"
            fill="#fff"
            stroke="#1976d2"
            strokeWidth="3"
          />
          <text
            x="364"
            y="376"
            fontSize="22"
            fontWeight="bold"
            fill="#1976d2"
            textAnchor="middle"
            alignmentBaseline="middle"
            style={{ fontFamily: 'Arial, sans-serif' }}
          >W</text>
        </g>
        {/* 파워포인트 파일 아이콘 */}
        <g>
          <rect
            x="420"
            y="336"
            width="48"
            height="64"
            rx="8"
            fill="#fff"
            stroke="#e64a19"
            strokeWidth="3"
          />
          <text
            x="444"
            y="376"
            fontSize="22"
            fontWeight="bold"
            fill="#e64a19"
            textAnchor="middle"
            alignmentBaseline="middle"
            style={{ fontFamily: 'Arial, sans-serif' }}
          >P</text>
        </g>
        {/* 한글 파일 아이콘 - 애니메이션 적용 */}
        <g style={{
          transition: 'transform 0.4s cubic-bezier(0.23,1,0.32,1)',
          transform: `scale(${hangeulScale})`,
          transformOrigin: '548px 400px'
        }}>
          <rect
            x="500"
            y="336"
            width="48"
            height="64"
            rx="8"
            fill="#fff"
            stroke="#0288d1"
            strokeWidth="3"
          />
          <text
            x="524"
            y="376"
            fontSize="22"
            fontWeight="bold"
            fill="#0288d1"
            textAnchor="middle"
            alignmentBaseline="middle"
            style={{ fontFamily: 'Arial, sans-serif' }}
          >ㅎ</text>
        </g>
      </g>
    </g>
  );
}

function ProductZCOutline_Haeker({ hackerX, hackerPhase }) {
  const hackerY = 110;
  // 흔들림(phase === 'shake')일 때만 빨간색, 아니면 흰색
  const hackerColor = hackerPhase === 'shake' ? '#e53935' : '#fff';

  return (
    <g
      style={{
        transition: 'transform 0.13s cubic-bezier(0.23,1,0.32,1)',
        transform: `translate(${hackerX + 40}px, ${hackerY}px) scale(0.8)`
      }}
    >
      <defs>
        <linearGradient id="hackerBodyGradient" x1="0" y1="190" x2="0" y2="247" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#444" />
          <stop offset="100%" stopColor="#222" />
        </linearGradient>
      </defs>
      <path
        d={`
          M-30 190
          A100 100 0 0 1 170 190
          Q150 220 140 247
          Q70 247 0 247
          Q-10 220 -30 190
          Z
        `}
        fill="#333"
        stroke="#888"
        strokeWidth="7"
      />
      <path
        d="
          M20 60
          Q20 20 70 10
          Q120 20 120 60
          Q120 110 70 140
          Q20 110 20 60
          Z
        "
        fill="#444"
        stroke="#444"
        strokeWidth="8"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <path
        d="
          M20 60
          Q20 20 70 10
          Q120 20 120 60
          Q120 110 70 140
          Q20 110 20 60
          Z
        "
        stroke={hackerColor}
        strokeWidth="8"
        fill="none"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <path
        d="M35 82
           Q70 152 105 82
           Q105 62 70 62
           Q35 62 35 82
           Z"
        stroke={hackerColor === '#fff' ? '#ccc' : '#ff8a80'}
        strokeWidth="3"
        fill="none"
      />
      <rect
        x="10"
        y="190"
        width="120"
        height="57"
        rx="12"
        stroke="#b74c14"
        strokeWidth="8"
        fill="#ff7043"
      />
    </g>
  );
}

export default ProductZCOutline;